import React, { useEffect, useState } from "react";
import { Alert, Input, Row, Col, Button } from "reactstrap";
import StepCompleteActionButton from "./StepCompleteActionButton";
import imageCompression from "browser-image-compression";

const DriverLicenseFlow = ({
    preview,
    email,
    rawText,
    onSubmit,
    onRetake
}) => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [fields, setFields] = useState({
        licenseNumber: "",
        lastName: "",
        firstName: "",
        dob: "",
        expiration: "",
        street: "",
        apt: "",
        city: "",
        state: "",
        zip: "",
        gender: ""
    });

    const updateField = (k, v) =>
        setFields(prev => ({ ...prev, [k]: v }));

    // -------------------------------------
    // OCR PARSER FOR DRIVER LICENSE
    // -------------------------------------
    const parseDriverLicense = (raw) => {
        const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);

        const getLine = (p) =>
            lines.find(l => l.toUpperCase().startsWith(p)) || "";

        let licenseNumber = getLine("DL").replace(/DL\s*/i, "");
        if (!licenseNumber) {
            const m = raw.match(/\b[A-Z]?\d{8}\b/i);
            if (m) {
                licenseNumber = m[0];
            }
        }
        const lastName = getLine("LN").replace(/LN\s*/i, "");
        const firstName = getLine("FN").replace(/FN\s*/i, "");
        const dob = getLine("DOB").replace(/DOB\s*/i, "");
        const expiration = getLine("EXP").replace(/EXP\s*/i, "");

        const fnIndex = lines.findIndex(l => l.toUpperCase().startsWith("FN"));
        const addressLine1 = lines[fnIndex + 1] || "";
        const addressLine2 = lines[fnIndex + 2] || "";

        const gender = (getLine("SEX")
            .replace(/SEX[^A-Z]*?/i, "")
            .match(/[MFX]/i) || [""])[0];

        let street = "";
        let apt = "";
        let city = "";
        let state = "";
        let zip = "";

        const aptMatch = addressLine1.match(/APT\s*([A-Z0-9\-]+)/i);
        if (aptMatch) {
            apt = aptMatch[1];
            street = addressLine1.replace(aptMatch[0], "").trim();
        } else {
            street = addressLine1.trim();
        }

        const zipMatch = addressLine2.match(/\b\d{5}(?:-\d{4})?\b/);
        if (zipMatch) zip = zipMatch[0];

        const stateMatch = addressLine2.match(/\b[A-Z]{2}\b/);
        if (stateMatch) state = stateMatch[0];

        if (state) {
            city = addressLine2
                .split(state)[0]
                .trim()
                .replace(/[,]+$/, "");
        } else {
            city = addressLine2.trim();
        }

        setFields({
            licenseNumber,
            lastName,
            firstName,
            dob,
            expiration,
            street,
            apt,
            city,
            state,
            zip,
            gender
        });
    };

    useEffect(() => {
        if (rawText) parseDriverLicense(rawText);
    }, [rawText]);

    // -------------------------------------
    // FINAL SUBMIT
    // -------------------------------------
    const submitFinal = async () => {
        setError("");
        setLoading(true);

        const payload = {
            type: "driver_license",
            data: {
                ...fields,
                email
            }
        };

        try {
            const resp = await onSubmit(payload);
            if (!resp || resp.code !== 1) {
                setError(resp?.message || "Save failed");
                setLoading(false);
                return;
            }
        } catch (err) {
            setError("Unexpected error");
            setLoading(false);
        }
    };

    // -------------------------------------
    // UI
    // -------------------------------------
    return (
        <>
            {error && <Alert color="danger">{error}</Alert>}

            {/* <h1 className="text-dark mb-3 fw-semibold">Verify Driver License</h1> */}

            {preview && (
                <img
                    src={preview}
                    alt="license"
                    style={{
                        width: "100%",
                        borderRadius: 8,
                        marginBottom: 20
                    }}
                />
            )}

            <Row>
                <Col md={6}>
                    <label>Driver License Number</label>
                    <Input
                        value={fields.licenseNumber}
                        onChange={(e) =>
                            updateField("licenseNumber", e.target.value)
                        }
                        className="mb-3"
                    />
                </Col>
                <Col md={6}>
                    <label>Gender</label>
                    <select
                        className="form-select mb-3"
                        value={fields.gender}
                        onChange={(e) => updateField("gender", e.target.value)}
                    >
                        <option value="">Select</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="X">X / Other</option>
                    </select>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <label>Last Name</label>
                    <Input
                        className="mb-3"
                        value={fields.lastName}
                        onChange={(e) => updateField("lastName", e.target.value)}
                    />
                </Col>

                <Col md={6}>
                    <label>First Name</label>
                    <Input
                        className="mb-3"
                        value={fields.firstName}
                        onChange={(e) => updateField("firstName", e.target.value)}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <label>Date of Birth</label>
                    <Input
                        className="mb-3"
                        value={fields.dob}
                        onChange={(e) => updateField("dob", e.target.value)}
                        placeholder="MM/DD/YYYY"
                    />
                </Col>

                <Col md={6}>
                    <label>Expiration Date</label>
                    <Input
                        className="mb-3"
                        value={fields.expiration}
                        onChange={(e) => updateField("expiration", e.target.value)}
                        placeholder="MM/DD/YYYY"
                    />
                </Col>
            </Row>

            <Row>
                <Col md={8}>
                    <label>Street</label>
                    <Input
                        className="mb-3"
                        value={fields.street}
                        onChange={(e) => updateField("street", e.target.value)}
                    />
                </Col>
                <Col md={4}>
                    <label>Apt</label>
                    <Input
                        className="mb-3"
                        value={fields.apt}
                        onChange={(e) => updateField("apt", e.target.value)}
                    />
                </Col>
            </Row>

            <Row>
                <Col md={5}>
                    <label>City</label>
                    <Input
                        className="mb-3"
                        value={fields.city}
                        onChange={(e) => updateField("city", e.target.value)}
                    />
                </Col>

                <Col md={3}>
                    <label>State</label>
                    <Input
                        className="mb-3"
                        value={fields.state}
                        onChange={(e) =>
                            updateField("state", e.target.value.toUpperCase())
                        }
                        maxLength={2}
                    />
                </Col>

                <Col md={4}>
                    <label>Zip Code</label>
                    <Input
                        className="mb-4"
                        value={fields.zip}
                        onChange={(e) => updateField("zip", e.target.value)}
                    />
                </Col>
            </Row>

            <StepCompleteActionButton
                label={loading ? "SAVING..." : "SUBMIT"}
                onClick={submitFinal}
                disabled={loading}
            />

            <Button
                className="w-100 mt-2"
                color="secondary"
                onClick={onRetake}
            >
                RETAKE PICTURE
            </Button>
        </>
    );
};

export default DriverLicenseFlow;